import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import interventionService from '../../../services/interventionService';

const InterventionDeleteModal = ({ isOpen, intervention, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await interventionService.deleteIntervention(intervention.id);
      if (res.success) {
        onSuccess && onSuccess();
        onClose();
      } else {
        setError(res.message);
      }
    } catch {
      setError('Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirm('');
    setError('');
    onClose();
  };

  if (!isOpen || !intervention) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-red-600 to-red-700'} px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-white bg-opacity-20'} w-10 h-10 rounded-lg flex items-center justify-center mr-3`}>
                <i className="fas fa-trash text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Supprimer l'intervention</h2>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-red-100'} text-sm`}>Cette action est irréversible</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          <div className="p-6">
            {error && (
              <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-900 border-gray-800 text-gray-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle mr-3 text-red-500"></i>
                  <div>
                    <div className="font-medium">Erreur</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-xl p-6 mb-4`}>
              <div className="flex items-center mb-4">
                <div className={`${isDarkMode ? 'bg-red-900' : 'bg-red-100'} w-10 h-10 rounded-lg flex items-center justify-center mr-3`}>
                  <i className="fas fa-exclamation-triangle text-red-600"></i>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Suppression définitive</h3>
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} text-sm`}>Vous êtes sur le point de supprimer l'intervention <b>#{intervention.id}</b>.</p>
                </div>
              </div>
              <div className="mb-2 text-xs">
                Pour confirmer, tapez <span className="font-semibold">{intervention.id}</span> :
              </div>
              <input
                type="text"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                placeholder={`Tapez ${intervention.id}`}
              />
              {confirm && confirm !== String(intervention.id) && (
                <p className="text-red-600 text-sm mt-2">
                  <i className="fas fa-times-circle mr-1"></i>
                  Le texte ne correspond pas
                </p>
              )}
              {confirm === String(intervention.id) && (
                <p className="text-green-600 text-sm mt-2">
                  <i className="fas fa-check-circle mr-1"></i>
                  Confirmation valide
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`border-t px-6 py-4 flex-shrink-0 flex justify-end gap-3 ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
          <button
            type="button"
            onClick={handleClose}
            className={`px-6 py-3 rounded-lg transition-colors font-medium border ${isDarkMode ? 'text-gray-200 bg-gray-800 border-gray-700 hover:bg-gray-700' : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'}`}
          >
            <i className="fas fa-times mr-2"></i>
            Annuler
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={loading || confirm !== String(intervention.id)}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center ${
              loading || confirm !== String(intervention.id)
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : isDarkMode
                ? 'bg-red-700 hover:bg-red-800 text-white shadow-lg hover:shadow-xl'
                : 'bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Suppression...
              </>
            ) : (
              <>
                <i className="fas fa-trash mr-2"></i>
                Supprimer définitivement
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterventionDeleteModal;